import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getBalance, getDailyReport, getActivityChart, exportDashboard, getPayments } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Download, BarChart3, Calendar, Search } from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { createPDF, addTable } from '../lib/pdf';

const LOCATIONS = [
  { value: '_all', label: 'Todas' },
  { value: 'san_antolin', label: 'San Antolín' },
  { value: 'san_martin', label: 'San Martín' },
  { value: 'ria_niembro', label: 'Ría de Niembro' },
];

const LOCS = { san_antolin: 'San Antolín', san_martin: 'San Martín', ria_niembro: 'Ría de Niembro' };

export default function ReportsPage() {
  const { bizumRecipients } = useAuth();
  const CATS = useMemo(() => {
    const base = { bizum: 'Bizum', cash: 'Efectivo', caixa: 'Caixa', caja: 'Caja', camiseta: 'Camiseta' };
    (bizumRecipients||[]).forEach(r => { base[`bizum_${r.id}`] = `Bizum ${r.name}`; base[r.id] = r.name; });
    return base;
  }, [bizumRecipients]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [location, setLocation] = useState('_all');
  const [balance, setBalance] = useState(null);
  const [daily, setDaily] = useState(null);
  const [dailyDate, setDailyDate] = useState(new Date().toISOString().split('T')[0]);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [blurred, setBlurred] = useState(true);
  const [pendingAmount, setPendingAmount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);

  const fetchRange = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (location && location !== '_all') params.location = location;
      const [balRes, chartRes] = await Promise.all([getBalance(params), getActivityChart(params)]);
      setBalance(balRes.data);
      setChartData(chartRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo, location]);

  const fetchDaily = useCallback(async () => {
    try {
      const params = { date: dailyDate };
      if (location && location !== '_all') params.location = location;
      const { data } = await getDailyReport(params);
      setDaily(data);
    } catch (e) { console.error(e); }
  }, [dailyDate, location]);

  useEffect(() => { fetchRange(); }, [fetchRange]);
  useEffect(() => { fetchDaily(); }, [fetchDaily]);
  useEffect(() => {
    getPayments({ status: 'pending' }).then(({ data }) => {
      const pending = (data || []).filter(p => parseFloat(p.amount_pending || 0) > 0);
      setPendingAmount(pending.reduce((s, p) => s + parseFloat(p.amount_pending || 0), 0));
      setPendingCount(pending.length);
    }).catch(() => {});
  }, []);

  const fmt = (v) => `${(v || 0).toFixed(2)} €`;
  const bv = (v) => <span className={`inline-block transition-all duration-200 ${blurred ? 'blur-sm select-none' : ''}`}>{v}</span>;

  const handleExportExcel = async () => {
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (location) params.location = location;
      const { data } = await exportDashboard(params);
      const url = URL.createObjectURL(data); const a = document.createElement('a'); a.href = url; a.download = 'informe.xlsx'; a.click();
    } catch { toast.error('Error'); }
  };

  const handleExportPDF = () => {
    const doc = createPDF();
    doc.setFontSize(16); doc.text('Dashboard - SURF SKULL', 14, 18);
    doc.setFontSize(10); doc.text(`Periodo: ${dateFrom ? dateFrom.split('-').reverse().join('-') : 'inicio'} - ${dateTo ? dateTo.split('-').reverse().join('-') : 'hoy'} | Ubicación: ${LOCS[location] || 'Todas'}`, 14, 26);
    if (balance) {
      addTable(doc, { startY: 32, head: [['Concepto', 'Importe']], body: [
        ['Ingresos Brutos', fmt(balance.gross_income)], ['Gastos', fmt(balance.total_transactions_expenses)], ['Costes Monitores', fmt(balance.total_monitor_costs)], ['Balance Neto', fmt(balance.net_income)],
        ['Bizum', fmt(balance.income_bizum)], ['Efectivo', fmt(balance.income_cash)], ['Caixa', fmt(balance.income_caixa)],
        ['Gasto Caja', fmt(balance.expense_caja)], ['Gasto Caixa', fmt(balance.expense_caixa)], ...(bizumRecipients||[]).map(r => [`Gasto ${r.name}`, fmt(balance[`expense_${r.id}`])]),
        ['Total Cursos', String(balance.total_courses)], ['Total Participantes', String(balance.total_participants)], ['Total Alquileres', String(balance.total_rentals)],
      ], theme: 'grid', headStyles: { fillColor: [74, 109, 124] } });
    }
    if (daily?.transactions?.length) {
      const yPos = (doc.lastAutoTable?.finalY) || 100;
      doc.setFontSize(12); doc.text(`Detalle del día: ${dailyDate}`, 14, yPos + 10);
      addTable(doc, { startY: yPos + 15, head: [['Tipo', 'Categoría', 'Importe', 'Descripción', 'Observaciones']], body: daily.transactions.map(t => [t.type === 'income' ? 'Ingreso' : 'Gasto', CATS[t.category] || t.category, `${t.amount.toFixed(2)} €`, t.description || '', t.observations || '']), theme: 'grid', headStyles: { fillColor: [129, 178, 154] } });
    }
    doc.save('dashboard.pdf');
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      <div className="sticky top-0 z-10 bg-[#FCFBF9] pt-4 md:pt-8 pb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#1A2B3C]" style={{ fontFamily: 'Manrope' }}>Dashboard</h1>
          <p className="text-sm text-[#8A9BA8] mt-1">Análisis detallado y gráficos de actividad</p>
        </div>
        <div className="flex items-center flex-wrap gap-2">
          <div className="relative"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-[#8A9BA8]"/><Input placeholder="Buscar..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 border-[#E5E0D8] w-24 sm:w-24 sm:w-44 h-9" data-testid="reports-search"/></div>
<Button variant="outline" onClick={handleExportExcel} className="border-[#E5E0D8] text-[#4A5D6C]"><Download className="w-4 h-4 mr-2"/>Excel</Button>
          <Button variant="outline" onClick={handleExportPDF} className="border-[#E5E0D8] text-[#4A5D6C]"><Download className="w-4 h-4 mr-2"/>PDF</Button>
        </div>
      </div>

      <div className="flex gap-3 flex-wrap items-end">
        <div className="space-y-1"><Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Desde</Label><Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="border-[#E5E0D8] w-44"/></div>
        <div className="space-y-1"><Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Hasta</Label><Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="border-[#E5E0D8] w-44"/></div>
        <div className="space-y-1"><Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Ubicación</Label>
          <Select value={location} onValueChange={setLocation}><SelectTrigger className="border-[#E5E0D8] w-44"><SelectValue placeholder="Todas"/></SelectTrigger><SelectContent>{LOCATIONS.map(l=><SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent></Select>
        </div>
      </div>

      <Tabs defaultValue="range" className="w-full">
        <div className="flex items-center gap-2">
          <TabsList className="bg-[#F5F2ED]">
            <TabsTrigger value="range" className="data-[state=active]:bg-white"><BarChart3 className="w-4 h-4 mr-1.5"/>Rango</TabsTrigger>
            <TabsTrigger value="daily" className="data-[state=active]:bg-white"><Calendar className="w-4 h-4 mr-1.5"/>Diario</TabsTrigger>
            <TabsTrigger value="charts" className="data-[state=active]:bg-white"><BarChart3 className="w-4 h-4 mr-1.5"/>Gráficos</TabsTrigger>
          </TabsList>
          <button onClick={() => setBlurred(b => !b)} className="text-lg leading-none p-1.5 rounded-md hover:bg-[#F5F2ED] transition-colors" title={blurred ? 'Mostrar valores' : 'Ocultar valores'}>
            {blurred ? '👁️' : '🙈'}
          </button>
        </div>

        <TabsContent value="range" className="mt-4 space-y-6">
          {loading ? <div className="skeleton-pulse h-40 rounded-lg"/> : balance ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-[#4A6D7C] rounded-lg p-4 text-white"><p className="text-xs uppercase tracking-[0.2em] text-white/70">Balance Neto</p><p className="text-2xl font-extrabold tabular-nums mt-1">{bv(fmt(balance.net_income))}</p></div>
                <div className="bg-white border border-[#E5E0D8] rounded-lg p-4"><p className="text-xs uppercase tracking-[0.2em] text-[#81B29A]">Ingresos</p><p className="text-2xl font-extrabold text-[#1A2B3C] tabular-nums mt-1">{bv(fmt(balance.gross_income))}</p></div>
                <div className="bg-white border border-[#E5E0D8] rounded-lg p-4"><p className="text-xs uppercase tracking-[0.2em] text-[#E07A5F]">Gastos</p><p className="text-2xl font-extrabold text-[#1A2B3C] tabular-nums mt-1">{bv(fmt(balance.total_transactions_expenses))}</p></div>
                <div className="bg-white border border-[#E5E0D8] rounded-lg p-4"><p className="text-xs uppercase tracking-[0.2em] text-[#E2B47D]">Costes Monitores</p><p className="text-2xl font-extrabold text-[#1A2B3C] tabular-nums mt-1">{bv(fmt(balance.total_monitor_costs))}</p></div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
                <div className="bg-white border border-[#E5E0D8] rounded-lg p-2 sm:p-4"><p className="text-[9px] sm:text-xs uppercase tracking-[0.05em] sm:tracking-[0.2em] text-[#8A9BA8]">Cursos</p><p className="text-lg sm:text-xl font-bold text-[#1A2B3C] mt-1">{bv(balance.total_courses)}</p></div>
                <div className="bg-white border border-[#E5E0D8] rounded-lg p-2 sm:p-4"><p className="text-[9px] sm:text-xs uppercase tracking-[0.05em] sm:tracking-[0.2em] text-[#8A9BA8]">Participantes</p><p className="text-lg sm:text-xl font-bold text-[#1A2B3C] mt-1">{bv(balance.total_participants)}</p></div>
                <div className="bg-white border border-[#E5E0D8] rounded-lg p-2 sm:p-4"><p className="text-[9px] sm:text-xs uppercase tracking-[0.05em] sm:tracking-[0.2em] text-[#8A9BA8]">Alquileres</p><p className="text-lg sm:text-xl font-bold text-[#1A2B3C] mt-1">{bv(balance.total_rentals)}</p></div>
                <div className={`rounded-lg p-2 sm:p-4 border ${pendingAmount > 0 ? 'bg-red-50 border-red-200' : 'bg-white border-[#E5E0D8]'}`}>
                  <p className={`text-[9px] sm:text-xs uppercase tracking-[0.05em] sm:tracking-[0.2em] ${pendingAmount > 0 ? 'text-red-400' : 'text-[#8A9BA8]'}`}>Pendiente cobro</p>
                  <p className={`text-lg sm:text-xl font-bold mt-1 tabular-nums ${pendingAmount > 0 ? 'text-red-600' : 'text-[#1A2B3C]'}`}>{bv(fmt(pendingAmount))}</p>
                  {pendingCount > 0 && <p className="text-[9px] sm:text-xs text-red-400 mt-0.5">{pendingCount} reserva{pendingCount !== 1 ? 's' : ''}</p>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white border border-[#E5E0D8] rounded-lg p-4">
                  <h4 className="text-sm font-bold text-[#1A2B3C] mb-2">Desglose Ingresos</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between"><span className="text-[#8A9BA8]">Bizum</span><span className="font-semibold tabular-nums whitespace-nowrap">{bv(fmt(balance.income_bizum))}</span></div>
                    {(bizumRecipients||[]).map(r => balance[`income_bizum_${r.id}`] > 0 && <div key={r.id} className="flex justify-between pl-3"><span className="text-[#8A9BA8] text-xs">↳ {r.name}</span><span className="font-medium tabular-nums whitespace-nowrap text-sm">{bv(fmt(balance[`income_bizum_${r.id}`]))}</span></div>)}
                    <div className="flex justify-between"><span className="text-[#8A9BA8]">Efectivo</span><span className="font-semibold tabular-nums whitespace-nowrap">{bv(fmt(balance.income_cash))}</span></div>
                    <div className="flex justify-between"><span className="text-[#8A9BA8]">Caixa</span><span className="font-semibold tabular-nums whitespace-nowrap">{bv(fmt(balance.income_caixa))}</span></div>
                    {(bizumRecipients||[]).map(r => <div key={r.id} className="flex justify-between"><span className="text-[#8A9BA8]">{r.name}</span><span className="font-semibold tabular-nums whitespace-nowrap">{bv(fmt(balance[`income_${r.id}`]))}</span></div>)}
                  </div>
                </div>
                <div className="bg-white border border-[#E5E0D8] rounded-lg p-4">
                  <h4 className="text-sm font-bold text-[#1A2B3C] mb-2">Desglose Gastos</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between"><span className="text-[#8A9BA8]">Caja</span><span className="font-semibold tabular-nums whitespace-nowrap">{bv(fmt(balance.expense_caja))}</span></div>
                    <div className="flex justify-between"><span className="text-[#8A9BA8]">Caixa</span><span className="font-semibold tabular-nums whitespace-nowrap">{bv(fmt(balance.expense_caixa))}</span></div>
                    {(bizumRecipients||[]).map(r => <div key={r.id} className="flex justify-between"><span className="text-[#8A9BA8]">{r.name}</span><span className="font-semibold tabular-nums whitespace-nowrap">{bv(fmt(balance[`expense_${r.id}`]))}</span></div>)}
                    <div className="flex justify-between border-t pt-1 mt-1"><span className="text-[#E2B47D] font-medium">Monitores</span><span className="font-semibold tabular-nums whitespace-nowrap">{bv(fmt(balance.total_monitor_costs))}</span></div>
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </TabsContent>

        <TabsContent value="daily" className="mt-4 space-y-4">
          <div className="flex gap-3 items-end">
            <div className="space-y-1"><Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Fecha</Label><Input type="date" value={dailyDate} onChange={e => setDailyDate(e.target.value)} className="border-[#E5E0D8] w-44"/></div>
          </div>
          {daily ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="bg-white border border-[#E5E0D8] rounded-lg p-3"><p className="text-xs uppercase text-[#81B29A]">Ingresos</p><p className="text-lg font-bold tabular-nums">{bv(fmt(daily.summary.income))}</p></div>
                <div className="bg-white border border-[#E5E0D8] rounded-lg p-3"><p className="text-xs uppercase text-[#E07A5F]">Gastos</p><p className="text-lg font-bold tabular-nums">{bv(fmt(daily.summary.expenses))}</p></div>
                <div className="bg-white border border-[#E5E0D8] rounded-lg p-3"><p className="text-xs uppercase text-[#4A6D7C]">Neto</p><p className="text-lg font-bold tabular-nums">{bv(fmt(daily.summary.net))}</p></div>
                <div className="bg-white border border-[#E5E0D8] rounded-lg p-3"><p className="text-xs uppercase text-[#8A9BA8]">Cursos</p><p className="text-lg font-bold tabular-nums">{bv(daily.activity?.courses || 0)}</p></div>
                <div className="bg-white border border-[#E5E0D8] rounded-lg p-3"><p className="text-xs uppercase text-[#8A9BA8]">Alquileres</p><p className="text-lg font-bold tabular-nums">{bv(daily.activity?.rentals || 0)}</p></div>
              </div>
              {daily.transactions?.length > 0 ? (
                <div className="border border-[#E5E0D8] rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#F5F2ED]">
                        <TableHead className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Tipo</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Categoría</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Descripción</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Observaciones</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8] text-right">Importe</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {daily.transactions.filter(t => { if (!search) return true; const s = search.toLowerCase(); return [t.type === 'income' ? 'ingreso' : 'gasto', t.category, CATS[t.category], t.description, t.observations, t.amount?.toString()].some(f => f && f.toLowerCase().includes(s)); }).map((t, i) => (
                        <TableRow key={i}>
                          <TableCell className={`text-sm font-medium ${t.type === 'income' ? 'text-[#81B29A]' : 'text-[#E07A5F]'}`}>{t.type === 'income' ? 'Ingreso' : 'Gasto'}</TableCell>
                          <TableCell className="text-sm">{CATS[t.category] || t.category}</TableCell>
                          <TableCell className="text-sm text-[#4A5D6C]">{t.description || '-'}</TableCell>
                          <TableCell className="text-sm text-[#8A9BA8] italic">{t.observations || '-'}</TableCell>
                          <TableCell className="text-sm font-semibold text-right tabular-nums">{bv(`${t.amount.toFixed(2)} €`)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : <p className="text-sm text-[#8A9BA8]">Sin transacciones en este día</p>}
            </>
          ) : null}
        </TabsContent>

        <TabsContent value="charts" className="mt-4 space-y-6">
          <h3 className="text-lg font-bold text-[#1A2B3C]" style={{ fontFamily: 'Manrope' }}>Actividad Diaria</h3>
          {chartData.length === 0 ? <p className="text-sm text-[#8A9BA8]">No hay datos de actividad en el rango seleccionado</p> : (
            <div className="bg-white border border-[#E5E0D8] rounded-lg p-4">
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => { const p = d.split('-'); return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : d; }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E5E0D8' }} />
                  <Legend />
                  <Bar dataKey="courses" name="Cursos" fill="#4A6D7C" radius={[4,4,0,0]} />
                  <Bar dataKey="participants" name="Alumnos" fill="#81B29A" radius={[4,4,0,0]} />
                  <Bar dataKey="rentals" name="Alquileres" fill="#E2B47D" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
